import React, {
  forwardRef,
  RefObject,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { InferType } from "yup";
import { Divider } from "antd";
import { TFunction } from "i18next/typescript/t";

import BaseButton from "../../../Button/BaseButton";
import {
  BaseDropdownProps,
  CustomChoiceInputProps,
  Option,
  WithCustomOptions,
  WithCustomOptionsProps,
} from "../types";
import useCustomChoiceOptions from "@/components_v2/FormFields/Dropdown/hooks/useCustomChoiceOptions";

import "./styles.css";
import { BaseSelectRef } from "rc-select/lib/BaseSelect";

const HookFormInput = React.lazy(() =>
  import("@/components_v2/FormFields/ReactHookForm/fields").then((module) => ({
    default: module.HookFormInput,
  })),
);

const customInputSchema = (t: TFunction) =>
  yup.string().typeError(t("form.validation.itemNotValid"));

const CustomChoiceInput = forwardRef<any, CustomChoiceInputProps>(
  ({ onAddOption, schema, placeholder, buttonText }, ref) => {
    const { t } = useTranslation();

    const formSchema = yup.object().shape({
      label: schema || customInputSchema(t),
    });
    const form = useForm({
      resolver: yupResolver(formSchema),
      mode: "onChange",
      defaultValues: {
        label: "",
      },
    });
    type CustomInputSchema = InferType<typeof formSchema>;

    useEffect(() => {
      if (ref && form) {
        const inputRef = ref as RefObject<any>;
        inputRef.current = form;
      }
    }, [form]);

    const handleAddItem = (values: CustomInputSchema) => {
      const value = values.label?.trim();
      if (!value) {
        form.setError("label", {
          type: "empty",
          message: t("form.validation.itemRequired"),
        });
        return;
      }
      onAddOption(value);
      form.reset();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.stopPropagation();
        e.preventDefault();
        form.handleSubmit(handleAddItem)();
      }
    };

    const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();
      form.handleSubmit(handleAddItem)();
    };

    return (
      <>
        <Divider style={{ margin: "8px 0" }} />
        <div className="custom-choice-item">
          <Suspense fallback={<div>{t("labels.loading")}</div>}>
            <HookFormInput
              name="label"
              control={form.control}
              onKeyDown={handleKeyDown}
              placeholder={
                placeholder || t("form.placeholders.addCustomOption")
              }
            />
            <BaseButton
              size="small"
              leadingIcon={<PlusOutlined />}
              onClick={handleClick}
            >
              {buttonText || t("buttons.add")}
            </BaseButton>
          </Suspense>
        </div>
      </>
    );
  },
);

const withCustomChoice = <P extends BaseDropdownProps>(
  WrappedComponent: React.ComponentType<P>,
): WithCustomOptions<P> => {
  return forwardRef<BaseSelectRef, WithCustomOptionsProps<P>>((props, ref) => {
    const {
      options = [],
      value,
      onChange,
      dropdownRender,
      onDropdownVisibleChange,
      open,
      className,
      multiple,
      placeholder,
      buttonText,
      customChoiceOptions,
      onChangeCallback,
      ...rest
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const formRef = useRef<UseFormReturn | null>(null);

    const { customOptions, setCustomOptions } = useCustomChoiceOptions({
      initialOptions: options,
      initialValue: value,
    });

    const handleOpenChange = (visible: boolean) => {
      setIsOpen(visible);
      if (!visible && formRef.current) {
        formRef.current.clearErrors();
        formRef.current.reset();
      }

      if (onDropdownVisibleChange) {
        onDropdownVisibleChange(visible);
      }
    };

    const handleAddCustomOption = (label: string) => {
      const existingOption = customOptions.find(
        (option) => option.value === label || option.label === label,
      );

      let option;
      if (existingOption) {
        if (Array.isArray(value)) {
          if (!value.includes(existingOption.value)) {
            onChange([...value, existingOption.value]);
          }
        } else {
          onChange(existingOption.value);
        }
        option = existingOption;
      } else {
        const newOption = {
          value: label,
          label,
          custom: true,
        };
        setCustomOptions([...customOptions, newOption]);

        if (Array.isArray(value)) {
          onChange([...value, label]);
        } else {
          onChange(multiple ? [label] : label);
        }
        option = newOption;
      }

      if (onChangeCallback) {
        onChangeCallback(option);
      }

      if (!multiple) {
        setIsOpen(false);
      }
    };

    const enhancedDropdownRender = (menu: React.ReactElement) => {
      const renderedMenu = dropdownRender ? dropdownRender(menu) : menu;
      return (
        <>
          {renderedMenu}
          <CustomChoiceInput
            ref={formRef}
            onAddOption={handleAddCustomOption}
            schema={customChoiceOptions?.schema}
            placeholder={customChoiceOptions?.placeholder}
            buttonText={buttonText}
          />
        </>
      );
    };

    const handleChange = (newValue: number | string, option?: Option) => {
      if (onChange) {
        onChange(newValue, option);
      }

      if (formRef.current) {
        formRef.current.clearErrors();
      }
    };

    return (
      <WrappedComponent
        {...(rest as unknown as P)}
        className={`custom-choice-dropdown ${className || ""}`}
        options={customOptions}
        value={value}
        onChange={handleChange}
        onChangeCallback={onChangeCallback}
        dropdownRender={enhancedDropdownRender}
        open={open !== undefined ? open : isOpen}
        multiple={multiple}
        placeholder={placeholder}
        onDropdownVisibleChange={handleOpenChange}
        ref={ref}
      />
    );
  });
};

export default withCustomChoice;
